import React, { useEffect, useState } from 'react';
import {
  getMapTranslation,
  getSlugFromUrl,
  useFetchFGBySlug,
  useRequestFanAccessToProtectedFG,
  useUpdateRequestFanAccessToProtectedFG,
  useRequestFanAccessToProtectedWL,
  useUpdateRequestFanAccessToProtectedWL,
  useFetchWaitingList,
  queryClient,
  ProtectionCodeRejectedError,
} from '@seaters/data-access';
import { Button } from '@seaters/ui';
import { Alert, Card, Form, Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  RequestAccessStatus,
  waitingListsKeys,
  WLAccessModeEnum,
} from '@seaters/constants';
const { Title } = Typography;

export function ProtectedCodeModal({
  isProtectedCodeModalShown,
  setProtectedCodeModalShown,
}: {
  isProtectedCodeModalShown: boolean;
  setProtectedCodeModalShown: (v: boolean) => void;
}) {
  const slug = getSlugFromUrl();

  const { waitingListId = '' } = useParams<{ waitingListId: string }>();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const lang = language === 'nl_NL' || language === 'nl_BE' ? 'nl' : language;
  const { data: fanGroupBySlugData, isRefetching } = useFetchFGBySlug(
    slug ?? '',
    true
  );
  const { data: waitingList, isRefetching: isRefetchingWL } =
    useFetchWaitingList(waitingListId);

  const isWLProtected =
    waitingList?.accessMode === WLAccessModeEnum.CODE_PROTECTED;

  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState<string>('');

  const { mutate: requestAccess, isPending: isRequestLoading } =
    useRequestFanAccessToProtectedFG(fanGroupBySlugData?.id ?? '', slug ?? '');

  const { mutate: updateRequestAccess, isPending: isRequestUpdateLoading } =
    useUpdateRequestFanAccessToProtectedFG(
      fanGroupBySlugData?.id ?? '',
      slug ?? ''
    );

  const { mutate: requestAccessWL, isPending: isRequestLoadingWL } =
    useRequestFanAccessToProtectedWL(waitingListId);

  const { mutate: updateRequestAccessWL, isPending: isRequestUpdateLoadingWL } =
    useUpdateRequestFanAccessToProtectedWL(waitingListId);

  const funcToRequest = isWLProtected ? requestAccessWL : requestAccess;

  const funcToUpdateRequest = isWLProtected
    ? updateRequestAccessWL
    : updateRequestAccess;

  // For testing:
  // const { mutate: deleteRequestAccess, isLoading: isRequestDeleteLoading } =
  //   useDeleteRequestToJoinFG(fanGroupBySlugData?.id ?? '', slug ?? '');

  const [isAlertShown, setAlertShown] = useState(false);
  // A refusal arrives as a rejected promise, so the reason is known here — before any refetch of
  // the membership lands. Without it the modal would just stop, having shown nothing.
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);

  const onRequestSettled = {
    onSuccess: () => {
      setRefusalMessage(null);
      setAlertShown(true);
      setIsLoading(false);
      queryClient.invalidateQueries({
        queryKey: waitingListsKeys.detail(waitingListId),
      });
    },
    onError: (error: unknown) => {
      setRefusalMessage(
        error instanceof ProtectionCodeRejectedError
          ? t(error.messageKey)
          : t('error_other_info')
      );
      setAlertShown(true);
      setIsLoading(false);
      queryClient.invalidateQueries({
        queryKey: waitingListsKeys.detail(waitingListId),
      });
    },
  };

  const onSubmit = async () => {
    setIsLoading(true);
    if (waitingList?.request || fanGroupBySlugData?.membership.request) {
      funcToUpdateRequest({ code }, onRequestSettled);
    } else {
      funcToRequest({ code }, onRequestSettled);
    }
  };
  const description = fanGroupBySlugData?.protectionCodeExplanation
    ? fanGroupBySlugData?.protectionCodeExplanation
    : isWLProtected
    ? t('unlock_text_wishlist_reserved', {
        wishlist: getMapTranslation(waitingList?.experienceName, lang),
      })
    : t('unlock_text_fangroup_reserved', {
        fangroup: getMapTranslation(fanGroupBySlugData?.name, lang),
      });

  useEffect(() => {
    setIsLoading(
      isRequestLoading ||
        isRequestUpdateLoading ||
        isRefetching ||
        isRequestLoadingWL ||
        isRequestUpdateLoadingWL ||
        isRefetchingWL
    );
  }, [isRefetching, isRefetchingWL]);

  useEffect(() => {
    if (waitingList?.request?.status === RequestAccessStatus.ACCEPTED) {
      setProtectedCodeModalShown(false);
    }
  }, [waitingList?.request?.status]);

  return (
    <Modal
      title={
        <Title level={3}>
          {isWLProtected
            ? t('waitinglist_unlock_modal_title')
            : t('general_protected_group')}
        </Title>
      }
      onCancel={() => {
        setAlertShown(false);
        setProtectedCodeModalShown(false);
      }}
      open={isProtectedCodeModalShown}
      footer={null}
      width={400}
      centered
      style={{ maxHeight: '800px' }}
      destroyOnClose={true}
    >
      <Form layout="vertical">
        <Form.Item label={t('enter_access_code_text')}>
          <Input
            onChange={(e) => setCode(e.target.value)}
            placeholder={
              t('type_here_placeholder')
                ? (t('type_here_placeholder') as string)
                : 'Type here'
            }
          />
        </Form.Item>

        <div
          style={{
            width: '100%',
            display: 'flex',
            gap: '20px',
            marginBottom: '24px',
          }}
        >
          <Button
            style={{ flexGrow: 1 }}
            onClick={() => {
              setProtectedCodeModalShown(false);
              setAlertShown(false);
            }}
          >
            {t('button_text_cancel')}
          </Button>
          <Button
            style={{ flexGrow: 1 }}
            type="primary"
            onClick={onSubmit}
            // loading={isRequestLoading || isRequestUpdateLoading || isRefetching}
            loading={isLoading}
          >
            {t('button_text_send')}
          </Button>
          {/* For testing purpose: */}
          {/* <Button        
            style={{ flexGrow: 1 }}
            type="primary"
            onClick={() => deleteRequestAccess()}
            loading={isRequestDeleteLoading || isRefetching}
          >
            Delete request
          </Button> */}
        </div>
        {refusalMessage && isAlertShown && (
          <Alert
            style={{ marginBottom: 16 }}
            message={refusalMessage}
            type="error"
          />
        )}
        {!refusalMessage &&
          fanGroupBySlugData?.membership.request?.rejectionReason &&
          isAlertShown && (
            <Alert
              style={{ marginBottom: 16 }}
              // The stored reason is the platform's own English sentence; `t` returns it unchanged
              // when it is not a known key.
              message={t(fanGroupBySlugData.membership.request.rejectionReason)}
              type="error"
            />
          )}
        {!refusalMessage &&
          waitingList?.request?.rejectionReason &&
          isAlertShown && (
            <Alert
              style={{ marginBottom: 16 }}
              message={t(waitingList.request.rejectionReason)}
              type="error"
            />
          )}
        <Card>
          <Card.Meta description={description} />
        </Card>
      </Form>
    </Modal>
  );
}

export default ProtectedCodeModal;
